"use client";

import React, { useRef, useEffect, useMemo, useState, useCallback, memo } from "react";
import { motion, useMotionValue, animate } from "motion/react";
import { cn } from "@/lib/utils";
import { BookOpen, Maximize2, Loader2 } from "lucide-react";

export interface Chapter {
  title: string;
  startTime: number;
  endTime: number;
  description?: string;
}

interface ChaptersCarouselProps {
  chapters: Chapter[];
  currentTime: number;
  onSeek?: (seconds: number) => void;
  isLoading?: boolean;
  className?: string;
  backgroundTone?: "light" | "dark";
}

const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.round(seconds)}sec`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (remainingSeconds === 0) {
    return `${minutes}min`;
  }
  return `${minutes}m ${remainingSeconds}s`;
};

// 🚀 CSS gradient templates - avoids string interpolation on every update
const GRADIENT_LIGHT = (p: number) => `linear-gradient(to right, 
  rgba(0, 0, 0, 0.95) 0%, 
  rgba(0, 0, 0, 0.95) ${p * 0.95}%,
  rgba(51, 65, 85, 0.6) ${p}%,
  rgba(71, 85, 105, 0.2) ${p + 2}%,
  rgba(100, 116, 139, 0.1) 100%)`;

const GRADIENT_DARK = (p: number) => `linear-gradient(to right, 
  rgba(0, 0, 0, 0.95) 0%, 
  rgba(0, 0, 0, 0.95) ${p * 0.95}%,
  rgba(51, 65, 85, 0.6) ${p}%,
  rgba(71, 85, 105, 0.15) ${p + 2}%,
  rgba(71, 85, 105, 0.08) 100%)`;

const BG_LIGHT_INACTIVE = 'rgba(51, 65, 85, 0.5)';
const BG_DARK_INACTIVE = 'rgba(0, 0, 0, 0.7)';

interface ChapterCardProps {
  chapter: Chapter;
  index: number;
  isActive: boolean;
  isPast: boolean;
  onSeek?: (seconds: number) => void;
  backgroundTone?: "light" | "dark";
  cardRef?: (el: HTMLButtonElement | null) => void;
}

// 🚀 Memoized ChapterCard - only re-renders when chapter CHANGES (not progress)
const ChapterCard = memo<ChapterCardProps>(({
  chapter,
  index,
  isActive,
  isPast,
  onSeek,
  backgroundTone = "dark",
  cardRef,
}) => {
  const duration = chapter.endTime - chapter.startTime;
  const isLight = backgroundTone === "light";

  const handleClick = useCallback(() => {
    onSeek?.(chapter.startTime);
  }, [onSeek, chapter.startTime]);

  // Initial background - will be updated via ref for active chapter
  const initialBg = useMemo(() => {
    if (isPast) return isLight ? GRADIENT_LIGHT(100) : GRADIENT_DARK(100);
    if (!isActive) return isLight ? BG_LIGHT_INACTIVE : BG_DARK_INACTIVE;
    return isLight ? GRADIENT_LIGHT(0) : GRADIENT_DARK(0);
  }, [isActive, isPast, isLight]);

  return (
    <motion.button
      ref={cardRef}
      data-chapter-index={index}
      onClick={handleClick}
      className={cn(
        "relative flex-shrink-0 overflow-hidden rounded-2xl",
        "h-[56px] w-full",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      )}
      style={{ background: initialBg }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Left info section */}
      <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-center px-3 py-1">
        <div className="flex items-center gap-1.5">
          <BookOpen 
            className={cn(
              "h-3.5 w-3.5 flex-shrink-0",
              isLight ? "text-slate-100" : "text-white/90"
            )} 
          />
          <span 
            className={cn(
              "text-xs font-medium",
              isLight ? "text-slate-200" : "text-white/70"
            )}
          >
            {formatDuration(duration)}
          </span>
        </div>
        
        <span className={cn(
          "text-sm font-semibold line-clamp-1 text-left",
          "text-white"
        )}>
          {chapter.title}
        </span>
      </div>

      {/* Expand button */}
      {/* <div className={cn(
        "absolute right-2.5 top-1/2 -translate-y-1/2",
        "flex items-center justify-center",
        "h-7 w-7 rounded-full",
        "bg-slate-900/60"
      )}>
        <Maximize2 className="h-3.5 w-3.5 text-white/80" />
      </div> */}
    </motion.button>
  );
}, (prevProps, nextProps) => {
  // 🚀 Custom comparison: only re-render when these change
  // Progress updates are handled via direct DOM manipulation
  return (
    prevProps.chapter === nextProps.chapter &&
    prevProps.index === nextProps.index &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.isPast === nextProps.isPast &&
    prevProps.backgroundTone === nextProps.backgroundTone &&
    prevProps.onSeek === nextProps.onSeek
  );
});

ChapterCard.displayName = "ChapterCard";

export const ChaptersCarousel: React.FC<ChaptersCarouselProps> = ({
  chapters,
  currentTime,
  onSeek,
  isLoading = false,
  className,
  backgroundTone = "dark",
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardRefsMap = useRef<Map<number, HTMLButtonElement>>(new Map());
  const [containerWidth, setContainerWidth] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isUserBrowsing, setIsUserBrowsing] = useState(false);
  const userBrowsingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dragX = useMotionValue(0);
  
  // 🚀 Track previous active index to detect chapter changes
  const prevActiveIndexRef = useRef(-1);

  const CARD_GAP = 8;
  const PEEK_WIDTH = 32;
  const PADDING = 12;
  
  const cardWidth = useMemo(() => {
    if (containerWidth === 0) return 300;
    return containerWidth - PADDING * 2 - PEEK_WIDTH;
  }, [containerWidth]);

  // Measure container width
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateWidth = () => setContainerWidth(container.offsetWidth);
    updateWidth();
    
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (userBrowsingTimeoutRef.current) {
        clearTimeout(userBrowsingTimeoutRef.current);
      }
    };
  }, []);

  // 🚀 Calculate active chapter index (memoized)
  const activeChapterIndex = useMemo(() => {
    if (chapters.length === 0) return -1;
    for (let i = chapters.length - 1; i >= 0; i--) {
      if (currentTime >= chapters[i].startTime) {
        return i;
      }
    }
    return 0;
  }, [chapters, currentTime]);

  const isLight = backgroundTone === "light";

  // 🚀🚀🚀 CORE OPTIMIZATION: Update progress via direct DOM manipulation
  // This effect runs on every currentTime change but does NOT trigger React re-render
  useEffect(() => {
    if (chapters.length === 0 || activeChapterIndex < 0) return;

    const activeChapter = chapters[activeChapterIndex];
    const duration = activeChapter.endTime - activeChapter.startTime;
    
    if (duration <= 0) return;

    const elapsed = currentTime - activeChapter.startTime;
    const progress = Math.min(100, Math.max(0, (elapsed / duration) * 100));

    // 🚀 Direct DOM update - bypasses React entirely
    const activeCard = cardRefsMap.current.get(activeChapterIndex);
    if (activeCard) {
      activeCard.style.background = isLight ? GRADIENT_LIGHT(progress) : GRADIENT_DARK(progress);
    }

    // Handle chapter transition: update previous chapter to 100% and reset future chapters
    if (prevActiveIndexRef.current !== activeChapterIndex) {
      // Mark previous active chapter as complete (100%)
      if (prevActiveIndexRef.current >= 0 && prevActiveIndexRef.current < activeChapterIndex) {
        const prevCard = cardRefsMap.current.get(prevActiveIndexRef.current);
        if (prevCard) {
          prevCard.style.background = isLight ? GRADIENT_LIGHT(100) : GRADIENT_DARK(100);
        }
      }
      
      // Reset future chapters when going backward
      if (prevActiveIndexRef.current > activeChapterIndex) {
        for (let i = activeChapterIndex + 1; i <= prevActiveIndexRef.current; i++) {
          const futureCard = cardRefsMap.current.get(i);
          if (futureCard) {
            futureCard.style.background = isLight ? BG_LIGHT_INACTIVE : BG_DARK_INACTIVE;
          }
        }
      }

      prevActiveIndexRef.current = activeChapterIndex;
    }
  }, [currentTime, chapters, activeChapterIndex, isLight]);

  // Auto-scroll to active chapter when it changes
  useEffect(() => {
    if (isUserBrowsing) return;
    if (activeChapterIndex >= 0 && activeChapterIndex !== currentIndex) {
      setCurrentIndex(activeChapterIndex);
      const targetX = -activeChapterIndex * (cardWidth + CARD_GAP);
      animate(dragX, targetX, { type: "spring", stiffness: 300, damping: 30 });
    }
  }, [activeChapterIndex, cardWidth, dragX, isUserBrowsing, currentIndex]);

  // Handle drag end
  const handleDragEnd = useCallback((_: unknown, info: { offset: { x: number }; velocity: { x: number } }) => {
    const threshold = cardWidth / 4;
    const velocity = info.velocity.x;
    const offset = info.offset.x;
    
    let newIndex = currentIndex;
    
    if (offset < -threshold || velocity < -500) {
      newIndex = Math.min(currentIndex + 1, chapters.length - 1);
    } else if (offset > threshold || velocity > 500) {
      newIndex = Math.max(currentIndex - 1, 0);
    }
    
    setCurrentIndex(newIndex);
    const targetX = -newIndex * (cardWidth + CARD_GAP);
    animate(dragX, targetX, { type: "spring", stiffness: 300, damping: 30 });
    
    setIsUserBrowsing(true);
    if (userBrowsingTimeoutRef.current) {
      clearTimeout(userBrowsingTimeoutRef.current);
    }
    userBrowsingTimeoutRef.current = setTimeout(() => {
      setIsUserBrowsing(false);
    }, 5000);
  }, [currentIndex, cardWidth, chapters.length, dragX]);

  // 🚀 Ref callback for storing card references
  const createCardRef = useCallback((index: number) => (el: HTMLButtonElement | null) => {
    if (el) {
      cardRefsMap.current.set(index, el);
    } else {
      cardRefsMap.current.delete(index);
    }
  }, []);

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center py-4", className)}>
        <Loader2 className={cn(
          "h-5 w-5 animate-spin",
          isLight ? "text-slate-400" : "text-white/40"
        )} />
        <span className={cn(
          "ml-2 text-sm",
          isLight ? "text-slate-500" : "text-white/50"
        )}>
          Generating chapters...
        </span>
      </div>
    );
  }

  if (chapters.length === 0) {
    return null;
  }

  return (
    <div className={cn("relative overflow-hidden", className)}>
      <div
        ref={scrollContainerRef}
        className="relative w-full overflow-hidden"
        style={{ padding: `0 ${PADDING}px` }}
      >
        <motion.div
          className="flex"
          style={{ 
            x: dragX,
            gap: `${CARD_GAP}px`,
          }}
          drag="x"
          dragConstraints={{
            left: -(chapters.length - 1) * (cardWidth + CARD_GAP),
            right: 0,
          }}
          dragElastic={0.1}
          onDragEnd={handleDragEnd}
        >
          {chapters.map((chapter, index) => (
            <div
              key={`chapter-${index}-${chapter.startTime}`}
              style={{ width: cardWidth, flexShrink: 0 }}
            >
              <ChapterCard
                chapter={chapter}
                index={index}
                isActive={index === activeChapterIndex}
                isPast={index < activeChapterIndex}
                onSeek={onSeek}
                backgroundTone={backgroundTone}
                cardRef={createCardRef(index)}
              />
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default ChaptersCarousel;
